import type { ReactNode } from 'react';
import { Button } from '../atoms/button/button';
import { classNames } from 'tradera/utils/class-names';
import styles from './dropdown-menu.module.scss';
import { useSekPriceFormatter } from 'tradera/localization/use-sek-price-formatter';
import type { SystemBadge } from 'tradera/state/services/types/webapi-mytradera-generated';
import { Badge as BadgeComponent } from 'tradera/components/badge/badge';

type MenuItemProps = {
    alerts?: ReactNode[];
    bold?: boolean;
    border?: boolean;
    className?: string;
    follow?: boolean;
    gaAttributes?: Record<string, string>;
    highlight?: boolean;
    icon?: ReactNode;
    imageContainerClassName?: string;
    imageUrl?: string;
    inSPA?: boolean;
    label?: ReactNode;
    link?: string;
    badge?: SystemBadge;
    notify?: number;
    onClick?: () => void;
};
export const MenuItem = ({
    alerts,
    bold,
    border,
    className,
    follow = true,
    gaAttributes,
    highlight,
    icon,
    imageContainerClassName,
    imageUrl,
    label,
    link,
    badge,
    notify,
    onClick,
}: MenuItemProps) => {
    const containerClass = classNames(
        'd-flex flex-row align-items-center focus-visible-inner p-0 font-weight-normal text-left',
        styles.item,
        icon ? styles.itemWithIcon : styles.itemDefault,
        className,
        {
            'flex-wrap': alerts?.length,
        },
    );

    return (
        <li className={classNames(border && 'border-bottom')}>
            <Button
                theme="mainMenu"
                fluid
                className={containerClass}
                href={link}
                {...(!follow && { rel: 'nofollow' })}
                {...gaAttributes}
                onClick={onClick}>
                <MenuItemContent
                    bold={bold}
                    highlight={highlight}
                    icon={icon}
                    imageContainerClassName={imageContainerClassName}
                    imageUrl={imageUrl}
                    label={label}
                    badge={badge}
                    notify={notify}
                    alerts={alerts}
                />
            </Button>
        </li>
    );
};

type MenuItemContentProps = {
    icon?: ReactNode;
    imageContainerClassName?: string;
    bold?: boolean;
    highlight?: boolean;
    imageUrl?: string;
    label?: ReactNode;
    badge?: SystemBadge;
    notify?: number;
    alerts?: ReactNode[];
};

const MenuItemContent = ({
    icon,
    imageContainerClassName,
    bold,
    highlight,
    imageUrl,
    label,
    badge,
    notify,
    alerts,
}: MenuItemContentProps) => {
    return (
        <>
            {icon && (
                <span
                    className={classNames(
                        'd-flex align-items-center justify-content-center',
                        styles.itemImage,
                        imageContainerClassName,
                    )}
                    style={{ backgroundImage: imageUrl }}>
                    {highlight ? (
                        <span className="d-flex p-1 bg-branded rounded-circle">
                            {icon}
                        </span>
                    ) : (
                        icon
                    )}
                </span>
            )}
            <span
                className={classNames(
                    'flex-grow pr-1 text-wrap font-hansen',
                    bold && 'font-weight-bold',
                )}>
                {label}
            </span>
            <div className="ml-auto d-flex align-items-center">
                <Badge badge={badge} />
                {notify ? (
                    <BadgeComponent theme="dark" round className="mr-3">
                        {notify}
                    </BadgeComponent>
                ) : null}
            </div>
            {alerts}
        </>
    );
};

type BadgeProps = {
    badge?: SystemBadge;
};

const Badge = ({ badge }: BadgeProps) => {
    const formatPrice = useSekPriceFormatter();

    if (!badge?.value) return null;

    let displayValue = badge.value;
    if (badge.type === 'WalletBalance') {
        displayValue = formatPrice(badge.value);
    }

    return (
        <span className="d-flex text-inter-medium align-items-center mr-3">
            {displayValue}
        </span>
    );
};
